/**
 * GED Coffre-Fort UI helpers (DOC-003).
 *
 * Shared utilities for GED components (file size formatting, status colors, etc.)
 */

import type { GedFileStatus, GedFolder } from '@/hooks/useGed.types'

// ─── Tree node type ───────────────────────────────────────────────

export interface TreeNode extends GedFolder {
    children: TreeNode[]
}

export function buildTree(folders: GedFolder[]): TreeNode[] {
    const map = new Map<string, TreeNode>()
    const roots: TreeNode[] = []

    folders.forEach((f) => {
        map.set(f.id, { ...f, children: [] })
    })

    folders.forEach((f) => {
        const node = map.get(f.id)!
        if (f.parent_id && map.has(f.parent_id)) {
            map.get(f.parent_id)!.children.push(node)
        } else {
            roots.push(node)
        }
    })
    remonterLesBoucles(folders, map, roots)

    const sortChildren = (nodes: TreeNode[]) => {
        nodes.sort((a, b) => a.position - b.position)
        nodes.forEach((n) => sortChildren(n.children))
    }
    sortChildren(roots)

    return roots
}

/**
 * 🔴 Un dossier pris dans une BOUCLE de parents (A rangé sous B, B sous A) n'est
 * atteignable depuis AUCUNE racine : ni lui, ni ses sous-dossiers, ni leurs pièces ne
 * s'affichaient, et aucun geste de l'écran ne pouvait les en sortir. Le serveur refuse
 * désormais ce déplacement (422 `FOLDER_CYCLE_DETECTED`, 2026-09-28), deux déplacements
 * croisés compris (verrou d'entité) ; un arbre refermé AVANT ce refus reste possible.
 * Le premier membre de la boucle rencontré est donc REMONTÉ à la racine, détaché de son
 * parent (sinon le rendu récursif tournerait sans fin) : il redevient visible, et se
 * range ailleurs par les gestes ordinaires — à condition que le CLIQUER ne fige pas
 * l'onglet : le fil d'Ariane remonte lui aussi les parents (`cheminDuDossier`).
 */
function remonterLesBoucles(folders: GedFolder[], map: Map<string, TreeNode>, roots: TreeNode[]): void {
    const vus = new Set<string>()
    const marquer = (depart: TreeNode) => {
        const pile = [depart]
        while (pile.length > 0) {
            const noeud = pile.pop()!
            if (vus.has(noeud.id)) continue
            vus.add(noeud.id)
            pile.push(...noeud.children)
        }
    }
    roots.forEach(marquer)
    for (const f of folders) {
        if (vus.has(f.id)) continue
        // Injoignable : chacun de ses ancêtres est dans la liste et injoignable aussi (sinon
        // il le serait). La remontée tourne donc en rond — le premier nœud revu est dans la boucle.
        const remontee = new Set<string>()
        let courant = f.id
        while (!remontee.has(courant)) {
            remontee.add(courant)
            courant = map.get(courant)!.parent_id!
        }
        const membre = map.get(courant)!
        const parent = map.get(membre.parent_id!)!
        parent.children = parent.children.filter((enfant) => enfant.id !== membre.id)
        roots.push(membre)
        marquer(membre)
    }
}

/**
 * Le fil d'Ariane de `id` : ses ancêtres, de la racine jusqu'à lui, par `parent_id` sur
 * TOUS les dossiers — système compris, à dessein : un sous-dossier d'appel d'offres, que
 * l'arbre range à la racine, y nomme encore sa consultation.
 *
 * 🔴 Chaque dossier n'y entre qu'UNE fois. Sans cela, la remontée d'un membre d'une
 * boucle de parents (A sous B, B sous A) ne s'arrêtait jamais : le premier clic sur le
 * dossier que `remonterLesBoucles` venait de rendre visible figeait l'onglet (mesuré le
 * 2026-09-28). Dans une boucle, le fil s'arrête au premier dossier déjà nommé.
 */
export function cheminDuDossier(folders: GedFolder[], id: string | null): GedFolder[] {
    const parId = new Map(folders.map((f) => [f.id, f]))
    const chemin: GedFolder[] = []
    const vus = new Set<string>()
    let courant = id
    while (courant && !vus.has(courant)) {
        vus.add(courant)
        const dossier = parId.get(courant)
        if (!dossier) break
        chemin.unshift(dossier)
        courant = dossier.parent_id
    }
    return chemin
}

// ─── Status badge config ────────────────────────────────────────────

export const STATUS_CONFIG: Record<GedFileStatus, { labelKey: string; className: string }> = {
    draft: {
        labelKey: 'ged.status.draft',
        className: 'bg-muted text-muted-foreground border-border',
    },
    active: {
        labelKey: 'ged.status.active',
        // DS : un seul vert = success (token light/dark auto). Pas d'emerald.
        className: 'bg-success-bg text-success border-success/30',
    },
    obsolete: {
        labelKey: 'ged.status.obsolete',
        className: 'bg-warning-bg text-warning border-warning/30',
    },
}

// ─── File size formatting ───────────────────────────────────────────

export function formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B'
    const units = ['B', 'KB', 'MB', 'GB', 'TB']
    const k = 1024
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    const value = bytes / Math.pow(k, i)
    return `${value < 10 ? value.toFixed(1) : Math.round(value)} ${units[i]}`
}

// ─── MIME type helpers ──────────────────────────────────────────────

export function isPreviewable(mimeType: string): boolean {
    return (
        mimeType === 'application/pdf' ||
        mimeType.startsWith('image/') ||
        mimeType.startsWith('video/') ||
        mimeType.startsWith('audio/')
    )
}

export type PreviewType = 'image' | 'pdf' | 'video' | 'audio'

export function getPreviewType(mimeType: string): PreviewType | null {
    if (mimeType === 'application/pdf') return 'pdf'
    if (mimeType.startsWith('image/')) return 'image'
    if (mimeType.startsWith('video/')) return 'video'
    if (mimeType.startsWith('audio/')) return 'audio'
    return null
}

export function getFileIcon(mimeType: string): string {
    if (mimeType === 'application/pdf') return 'file-text'
    if (mimeType.startsWith('image/')) return 'image'
    if (mimeType.startsWith('video/')) return 'video'
    if (mimeType.startsWith('audio/')) return 'music'
    if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) return 'sheet'
    if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) return 'presentation'
    if (mimeType.includes('word') || mimeType.includes('document')) return 'file-text'
    if (mimeType.includes('zip') || mimeType.includes('compressed')) return 'archive'
    return 'file'
}
